<script setup lang="ts">
import { computed } from 'vue';

type PropsType = {

    /** Number of columns (default: 4) */
    columns?: 1 | 2 | 3 | 4 | 5;

    /** Gap size between items */
    gap?: 'sm' | 'md' | 'lg';

    /** Enable responsive behavior (default: true) */
    responsive?: boolean;
    classes?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    columns: 4,
    gap: 'md',
    responsive: true,
});

const gapSizes: Record<string, string> = {
    sm: '8px',
    md: '16px',
    lg: '24px',
};

const gridStyle = computed(() => {
    const styles: Record<string, string> = {
        display: 'grid',
        gap: gapSizes[props.gap],
    };

    if (!props.responsive) {
        styles.gridTemplateColumns = `repeat(${props.columns}, 1fr)`;
    }

    return styles;
});

const gridClass = computed(() => {
    const classes = ['b-dashboard-grid'];

    if (props.responsive) {
        classes.push(`b-dashboard-grid--cols-${props.columns}`);
    }

    if (props.classes) {
        classes.push(props.classes);
    }

    return classes;
});

defineOptions({ inheritAttrs: false });
</script>

<template>
    <div
        :class="gridClass"
        :style="gridStyle"
        v-bind="$attrs"
    >
        <slot />
    </div>
</template>

<style lang="scss" scoped>
.b-dashboard-grid {
    width: 100%;
}

.b-dashboard-grid--cols-5 {
    grid-template-columns: repeat(5, 1fr);

    /* Five tiles need ~1100 px of content next to the sidebar. */
    @media (max-width: 1360px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
}

.b-dashboard-grid--cols-4 {
    grid-template-columns: repeat(4, 1fr);

    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.b-dashboard-grid--cols-3 {
    grid-template-columns: repeat(3, 1fr);

    @media (max-width: 1000px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.b-dashboard-grid--cols-2 {
    grid-template-columns: repeat(2, 1fr);

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.b-dashboard-grid--cols-1 {
    grid-template-columns: 1fr;
}

/* Column span utilities */
.b-dashboard-grid--cols-3 {
    /* 2/3 and 1/3 layout */
    :deep(.b-dashboard-grid__col-2-3) {
        grid-column: span 2;
    }

    :deep(.b-dashboard-grid__col-1-3) {
        grid-column: span 1;
    }
}

/* Responsive adjustments */
@media (max-width: 1000px) {
    .b-dashboard-grid--cols-3 {
        :deep(.b-dashboard-grid__col-2-3) {
            grid-column: span 1;
        }
    }
}

@media (max-width: 768px) {
    .b-dashboard-grid--cols-3 {
        :deep(.b-dashboard-grid__col-2-3),
        :deep(.b-dashboard-grid__col-1-3) {
            grid-column: span 1;
        }
    }
}
</style>
